import Phaser from 'phaser';
import { ANIMATION } from '@/config/balance.config';

/**
 * Animator dla dekoracji biomu (kwiatki, krzaki, drzewka).
 *
 * Stosuje:
 *  - Idle sway — delikatne kołysanie (sin t * amplitude) z losową fazą per instancja,
 *    żeby 16 dekoracji nie wahało się synchronicznie. Kwiaty bujają się mocniej (~4°),
 *    krzaki delikatniej (~1.5°).
 *  - Idle breath — bardzo subtelny scale puls.
 *  - Touch push — gdy gracz lub pet jest w promieniu `touchPushRadius`, dekoracja
 *    chyla się W KIERUNKU od entity (jakby ją odepchnął). Wraca smooth-damp.
 *  - Contact sparkle — przy pierwszym wejściu w obszar, spawnuje 3 cząstki
 *    (tinted color per typ — różowe dla pink flower, żółte dla yellow, zielone dla bush).
 *
 * Kwiaty mają origin (0.5, 1.0) żeby kołysały się od BASE (stopy) — naturalny wind feel.
 * Krzaki zostają z (0.5, 0.5) — pivot pośrodku, bo są chunky.
 */
export class DecorationAnimator {
  private readonly phaseOffset: number = Math.random() * Math.PI * 2;
  private readonly baseScale: number;
  private readonly swayAmplitude: number;
  private readonly contactTint: number;

  private currentPushTilt = 0;
  private wasNearLastFrame = false;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly sprite: Phaser.GameObjects.Image,
    textureKey: string,
  ) {
    const cfg = ANIMATION.decoration;
    const perKey = cfg.perKey[textureKey];
    const isFlower = perKey?.isFlower ?? false;
    this.swayAmplitude = perKey?.swayAmplitudeRad ?? cfg.swayAmplitudeRadDefault;
    // Tint dymków kontaktu: perKey (specyficzny dla dekoracji) → fallback po prefiksie
    // biomu (np. deco_atlantis_* → niebieski) → biały default.
    const prefixTint = Object.entries(cfg.contactTintByPrefix)
      .find(([prefix]) => textureKey.startsWith(prefix))?.[1];
    this.contactTint = perKey?.contactTint ?? prefixTint ?? 0xffffff;

    this.baseScale = sprite.scaleX;     // zapamiętaj aktualną skalę jako bazową

    // Kwiaty: re-pivot na bottom-center żeby kołysały się od BASE (wind feel).
    // Krzaki/inne: zostawiamy origin (0.5, 0.5) — pivot pośrodku, mniejsze kołysanie.
    if (isFlower) {
      const halfHeight = sprite.displayHeight * 0.5;
      sprite.setOrigin(0.5, 1.0);
      sprite.y += halfHeight;          // kompensacja, żeby sprite stał w tej samej pozycji wizualnie
    }
  }

  /**
   * Aktualizuje sway + breath + touch push.
   * @param entities lista pozycji {x,y} — gracz + wszystkie equipped pety
   */
  update(time: number, entities: ReadonlyArray<{ x: number; y: number }>): void {
    const cfg = ANIMATION.decoration;

    // 1) Znajdź najbliższe entity (player + pety)
    let nearestDist = Infinity;
    let nearestDx = 0;       // sprite.x - entity.x — dodatnia gdy entity po lewej
    for (const e of entities) {
      const dx = this.sprite.x - e.x;
      const dy = this.sprite.y - e.y;
      const d = Math.hypot(dx, dy);
      if (d < nearestDist) {
        nearestDist = d;
        nearestDx = dx;
      }
    }

    // 2) Idle sway + breath
    const t = time / 1000 + this.phaseOffset;
    const idleSway = Math.sin(t * Math.PI * 2 * cfg.swayFrequencyHz) * this.swayAmplitude;
    const breath = 1 + Math.sin(t * Math.PI * 2 * cfg.breathFrequencyHz) * cfg.breathAmplitude;

    // 3) Touch push — entity w promieniu odpycha dekorację
    let targetPushTilt = 0;
    if (nearestDist < cfg.touchPushRadius) {
      const intensity = 1 - nearestDist / cfg.touchPushRadius;
      // sign(nearestDx) — entity po lewej (dx>0) → tilt do prawej (dodatnie); po prawej → ujemne
      const pushDir = nearestDx >= 0 ? 1 : -1;
      targetPushTilt = pushDir * intensity * cfg.touchPushIntensityRad;
    }
    this.currentPushTilt += (targetPushTilt - this.currentPushTilt) * cfg.touchRecoverLerp;

    // 4) Aplikuj rotation + scale (breath × base)
    this.sprite.rotation = idleSway + this.currentPushTilt;
    this.sprite.scaleX = this.baseScale * breath;
    this.sprite.scaleY = this.baseScale * breath;

    // 5) Contact sparkle — przy pierwszym wejściu w 80% promienia (a nie cały promień —
    //    inaczej spawn'owało by się przy każdym lekkim przesunięciu na granicy)
    const isNear = nearestDist < cfg.touchPushRadius * 0.8;
    if (isNear && !this.wasNearLastFrame) {
      this.spawnContactSparkle();
    }
    this.wasNearLastFrame = isNear;
  }

  private spawnContactSparkle(): void {
    if (!this.scene.textures.exists('fx-particle-white')) return;
    const sp = ANIMATION.decoration.contactSparkle;

    // Particle spawn point: górna część sprite'a (kwiat ma kwiat na górze; bush ma liście)
    const yOffset = this.sprite.originY === 1.0
      ? -this.sprite.displayHeight * 0.5        // pivot bottom → ujemny offset to góra
      : -this.sprite.displayHeight * 0.25;       // pivot center → trochę nad środkiem

    const emitter = this.scene.add.particles(this.sprite.x, this.sprite.y + yOffset, 'fx-particle-white', {
      speed: { min: sp.speedMin, max: sp.speedMax },
      lifespan: sp.lifespanMs,
      scale: { start: sp.scaleStart, end: sp.scaleEnd },
      quantity: sp.count,
      gravityY: sp.gravityY,
      angle: { min: sp.angleMin, max: sp.angleMax },
      tint: this.contactTint,
      emitting: false,
    });
    // ⭐ Wysoki depth żeby były WIDOCZNE — wcześniej były pod dekoracją (-50+1 = -49)
    emitter.setDepth(sp.depth);
    emitter.explode(sp.count);
    this.scene.time.delayedCall(sp.lifespanMs + 100, () => emitter.destroy());
  }
}
